import React, { useEffect, useState, useCallback } from "react";
import { CountryImprovement } from "./CountryImprovement";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldAlert, Loader2, Users, Gamepad2, Trophy, Target, Download, RefreshCw,
  UsersRound, Activity, TrendingUp, TrendingDown, LineChart as LineChartIcon, Repeat2,
  Layers, ArrowUpRight, ArrowDownRight, Minus, ChevronRight, Mail,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell,
} from "recharts";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, fmt, CATEGORIES } from "../../lib/game";
import { useAuth } from "../../context/AuthContext";

const Stat = ({ Icon, label, value, accent = "text-emerald-400" }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/70 px-4 py-3.5">
    <Icon className={`w-4 h-4 ${accent} mb-2`} />
    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className="font-mono text-xl font-bold text-slate-100" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
  </div>
);

export const AdminDashboard = ({ onSignIn }) => {
  const { user } = useAuth();
  const isAdmin = user && typeof user === "object" && user.role === "admin";
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [perf, setPerf] = useState(null);
  const [groups, setGroups] = useState([]);
  const [countryStats, setCountryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [days, setDays] = useState(0);
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfExporting, setPerfExporting] = useState(false);
  const [activeConcept, setActiveConcept] = useState(null);
  const [conceptData, setConceptData] = useState(null);
  const [conceptLoading, setConceptLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/sessions?limit=50"),
      api.get("/admin/performance"),
      api.get("/admin/groups"),
      api.get("/admin/countries"),
    ])
      .then(([ov, ss, pf, gr, ct]) => {
        setCountryStats(ct.data?.countries || []);
        setOverview(ov.data);
        setSessions(ss.data || []);
        setPerf(pf.data);
        setGroups(gr.data?.groups || []);
      })
      .catch(() => toast.error("Couldn't load admin data"))
      .finally(() => setLoading(false));
  }, []);

  const changeDays = useCallback((d) => {
    setDays(d);
    setPerfLoading(true);
    Promise.all([api.get(`/admin/performance?days=${d}`), api.get(`/admin/groups?days=${d}`), api.get(`/admin/countries?days=${d}`)])
      .then(([pf, gr, ct]) => {
        setCountryStats(ct.data?.countries || []);
        setPerf(pf.data);
        setGroups(gr.data?.groups || []);
      })
      .catch(() => toast.error("Couldn't load performance"))
      .finally(() => setPerfLoading(false));
  }, []);

  const openConcept = useCallback((tag) => {
    setActiveConcept(tag);
    setConceptData(null);
    setConceptLoading(true);
    api.get(`/admin/concept/${encodeURIComponent(tag)}?days=${days}`)
      .then(({ data }) => setConceptData(data))
      .catch(() => toast.error("Couldn't load concept detail"))
      .finally(() => setConceptLoading(false));
  }, [days]);

  const exportPerf = async () => {
    setPerfExporting(true);
    try {
      const res = await api.get(`/admin/performance/export?days=${days}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `burgeon-performance-${days ? `${days}d` : "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Performance CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setPerfExporting(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin, load]);

  const doExport = async (anonymised) => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/export?anonymised=${anonymised}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `burgeon-${anonymised ? "anonymised" : "identifiable"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-16">
        <div className="max-w-md mx-auto text-center rounded-3xl border border-slate-800 bg-slate-900/70 p-8" data-testid="admin-access-denied">
          <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-slate-100">Admin only</h2>
          <p className="text-slate-400 text-sm mt-2">This area is restricted to Burgeon administrators.</p>
          {!(user && typeof user === "object") && (
            <Button onClick={onSignIn} className="mt-5 bg-slate-100 text-slate-900 hover:bg-white font-semibold">Sign In</Button>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-61px)] flex items-center justify-center text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading admin dashboard…
      </div>
    );
  }

  const t = overview?.totals || {};
  const catData = Object.entries(overview?.category_counts || {}).map(([k, v]) => ({
    name: CATEGORIES[k]?.label || k,
    value: v,
    fill: CATEGORIES[k]?.hex || "#10B981",
  }));
  const signupData = (overview?.signups_14d || []).map((s) => ({ date: s.date.slice(5), count: s.count }));

  const p = perf?.performance || {};
  const imp = perf?.improvement || {};
  const distData = (perf?.distribution || []).map((d) => ({ name: d.band, value: d.count }));
  const DIST_COLORS = ["#ef4444", "#f59e0b", "#10B981", "#14b8a6", "#3b82f6", "#a78bfa"];
  const curveData = (imp.learning_curve || []).map((c) => ({ game: `Game ${c.game}`, value: c.avg_net_worth, players: c.players }));
  const concepts = perf?.concept_mastery || [];
  const strengths = concepts.filter((c) => c.avg_impact >= 0).slice(0, 8);
  const struggles = concepts.filter((c) => c.avg_impact < 0).slice().reverse().slice(0, 8);
  const DAY_OPTS = [{ v: 0, l: "All time" }, { v: 7, l: "7 days" }, { v: 30, l: "30 days" }, { v: 90, l: "90 days" }];

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-8" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-50 flex items-center gap-2">
              <ShieldAlert className="w-7 h-7 text-emerald-400" /> Admin Dashboard
            </h1>
            <p className="text-slate-400 text-sm mt-1">Platform-wide overview across every player, group and household.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button data-testid="admin-test-weekly-btn" variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
              onClick={() => api.post("/admin/reminders/test")
                .then(({ data }) => toast.success(`Test weekly recap sent to ${data.sent_to}`))
                .catch((e) => toast.error(e.response?.data?.detail || "Couldn't send the test email"))}>
              <Mail className="w-4 h-4 mr-1.5" /> Test weekly recap
            </Button>
            <Button data-testid="admin-refresh-btn" onClick={load} variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat Icon={Users} label="Users" value={t.users ?? 0} />
          <Stat Icon={Gamepad2} label="Sessions" value={t.sessions ?? 0} accent="text-blue-400" />
          <Stat Icon={Trophy} label="Completed" value={t.completed_sessions ?? 0} accent="text-yellow-400" />
          <Stat Icon={Activity} label="Decisions" value={t.total_decisions ?? 0} accent="text-purple-400" />
          <Stat Icon={UsersRound} label="Groups" value={t.groups ?? 0} accent="text-teal-400" />
          <Stat Icon={UsersRound} label="Households" value={t.households ?? 0} accent="text-pink-400" />
          <Stat Icon={Trophy} label="Avg Net Worth" value={fmt(t.avg_net_worth ?? 0)} />
          <Stat Icon={Target} label="Follow Through" value={overview?.follow_through_rate != null ? `${overview.follow_through_rate}%` : "—"} accent="text-emerald-400" />
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
            <h3 className="font-heading text-lg font-bold text-slate-100 mb-3">Decisions by category</h3>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={catData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#e2e8f0" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
            <h3 className="font-heading text-lg font-bold text-slate-100 mb-3">Sign-ups (last 14 days)</h3>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={signupData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#e2e8f0" }} />
                  <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
            <h3 className="font-heading text-lg font-bold text-slate-100 mb-3">Coach persona mix</h3>
            <div className="space-y-2">
              {(overview?.persona_counts || []).slice(0, 6).map(([p, n]) => (
                <div key={p} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">{p}</span>
                  <span className="font-mono text-slate-400">{n}</span>
                </div>
              ))}
              {(!overview?.persona_counts || overview.persona_counts.length === 0) && (
                <p className="text-slate-500 text-sm">No completed reviews yet.</p>
              )}
            </div>
          </div>
          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
            <h3 className="font-heading text-lg font-bold text-slate-100 mb-3">Life-stage distribution</h3>
            <div className="space-y-2">
              {(overview?.life_stage_counts || []).slice(0, 7).map(([s, n]) => (
                <div key={s} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300 capitalize">{String(s).replace(/_/g, " ")}</span>
                  <span className="font-mono text-slate-400">{n}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- Anonymised Player Performance & Improvement ---- */}
        <div className="mt-8" data-testid="admin-performance">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <LineChartIcon className="w-5 h-5 text-emerald-400" />
                <h2 className="font-heading text-2xl font-bold text-slate-50">Player Performance &amp; Improvement</h2>
                {perfLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
              </div>
              <p className="text-slate-400 text-sm">
                {days ? `Last ${days} days` : "All completed games"} — fully anonymised, no player names or emails.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex rounded-xl border border-slate-700 bg-slate-900 p-1" data-testid="perf-day-filter">
                {DAY_OPTS.map((o) => (
                  <button
                    key={o.v}
                    data-testid={`perf-days-${o.v}`}
                    onClick={() => changeDays(o.v)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      days === o.v ? "bg-emerald-500 text-slate-950" : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <Button data-testid="perf-export-btn" onClick={exportPerf} disabled={perfExporting}
                variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 h-9 text-xs">
                <Download className="w-3.5 h-3.5 mr-1.5" /> {perfExporting ? "Exporting…" : "Download CSV"}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat Icon={Trophy} label="Avg Net Worth" value={fmt(p.avg_net_worth ?? 0)} />
            <Stat Icon={Target} label="Median Net Worth" value={fmt(p.median_net_worth ?? 0)} accent="text-teal-400" />
            <Stat Icon={p.avg_change >= 0 ? TrendingUp : TrendingDown} label="Avg Change" value={fmt(p.avg_change ?? 0)} accent={p.avg_change >= 0 ? "text-emerald-400" : "text-red-400"} />
            <Stat Icon={TrendingUp} label="Grew Net Worth" value={p.grew_pct != null ? `${p.grew_pct}%` : "—"} accent="text-emerald-400" />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
              <h3 className="font-heading text-lg font-bold text-slate-100 mb-3">Outcome distribution (final net worth)</h3>
              <div style={{ width: "100%", height: 230 }}>
                <ResponsiveContainer>
                  <BarChart data={distData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
                    <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#e2e8f0" }} formatter={(v) => [`${v} players`, "Count"]} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {distData.map((d, i) => <Cell key={i} fill={DIST_COLORS[i % DIST_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
              <h3 className="font-heading text-lg font-bold text-slate-100 mb-1">Learning curve</h3>
              <p className="text-xs text-slate-500 mb-3">Average final net worth by how many games a player has completed.</p>
              {curveData.length > 1 ? (
                <div style={{ width: "100%", height: 205 }}>
                  <ResponsiveContainer>
                    <LineChart data={curveData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="game" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#e2e8f0" }} formatter={(v, n, pr) => [fmt(v), `Avg (${pr?.payload?.players} players)`]} />
                      <Line type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-slate-500 text-sm py-8 text-center">Not enough repeat games yet to chart a learning curve.</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
            <Stat Icon={Repeat2} label="Returning Players" value={imp.returning_players ?? 0} accent="text-pink-400" />
            <Stat Icon={TrendingUp} label="Improved" value={imp.improved_pct != null ? `${imp.improved_pct}%` : "—"} accent="text-emerald-400" />
            <Stat Icon={imp.avg_improvement >= 0 ? TrendingUp : TrendingDown} label="Avg Improvement" value={fmt(imp.avg_improvement ?? 0)} accent={imp.avg_improvement >= 0 ? "text-emerald-400" : "text-red-400"} />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
              <h3 className="font-heading text-lg font-bold text-slate-100 mb-3">Average change by life stage</h3>
              <div className="space-y-2">
                {(perf?.by_life_stage || []).map((row) => (
                  <div key={row.stage} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300 capitalize">{String(row.stage).replace(/_/g, " ")} <span className="text-slate-600">· {row.count}</span></span>
                    <span className={`font-mono font-semibold ${row.avg_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmt(row.avg_change)}</span>
                  </div>
                ))}
                {(!perf?.by_life_stage || perf.by_life_stage.length === 0) && <p className="text-slate-500 text-sm">No completed games yet.</p>}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5">
              <h3 className="font-heading text-lg font-bold text-slate-100 mb-1">Improvement by player (anonymised)</h3>
              <p className="text-xs text-slate-500 mb-3">First game vs latest game — no names, players are numbered.</p>
              {(imp.table || []).length > 0 ? (
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-sm" data-testid="admin-improvement-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                        <th className="py-2 pr-3 font-semibold">Player</th>
                        <th className="py-2 pr-3 font-semibold text-center">Games</th>
                        <th className="py-2 pr-3 font-semibold text-right">First</th>
                        <th className="py-2 pr-3 font-semibold text-right">Latest</th>
                        <th className="py-2 pr-3 font-semibold text-right">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {imp.table.map((r) => (
                        <tr key={r.label} className="border-b border-slate-800/60">
                          <td className="py-2 pr-3 text-slate-200">{r.label}</td>
                          <td className="py-2 pr-3 text-center text-slate-400">{r.games}</td>
                          <td className="py-2 pr-3 text-right font-mono text-slate-400">{fmt(r.first_net_worth)}</td>
                          <td className="py-2 pr-3 text-right font-mono text-slate-200">{fmt(r.latest_net_worth)}</td>
                          <td className={`py-2 pr-3 text-right font-mono font-semibold ${r.delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {r.delta >= 0 ? "+" : ""}{fmt(r.delta)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-sm py-8 text-center">No players have completed two or more games yet.</p>
              )}
            </div>
          </div>

          {/* Concept mastery — which concepts players handle well vs struggle with */}
          <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="admin-concept-mastery">
            <div className="rounded-3xl border border-emerald-500/25 bg-emerald-500/[0.04] p-5">
              <h3 className="font-heading text-lg font-bold text-slate-100 mb-1 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" /> Concepts handled well
              </h3>
              <p className="text-xs text-slate-500 mb-3">Highest average net-worth impact when this concept comes up.</p>
              {strengths.length > 0 ? (
                <div className="space-y-2">
                  {strengths.map((c) => (
                    <button key={c.concept} data-testid={`concept-row-${c.concept.replace(/\s+/g, "-")}`}
                      onClick={() => openConcept(c.concept)}
                      className="w-full flex items-center justify-between gap-2 text-sm rounded-lg px-2 py-1.5 -mx-2 hover:bg-emerald-500/10 transition-colors group text-left">
                      <span className="text-slate-300 capitalize truncate flex items-center gap-1">
                        <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-emerald-400 transition-colors" />
                        {c.concept} <span className="text-slate-600">· {c.count}</span>
                      </span>
                      <span className="flex items-center gap-3 shrink-0">
                        <span className="text-[11px] text-slate-500">{c.positive_pct}% good</span>
                        <span className="font-mono font-semibold text-emerald-400 w-16 text-right">+{fmt(c.avg_impact)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm py-6 text-center">Not enough decisions yet.</p>
              )}
            </div>
            <div className="rounded-3xl border border-red-500/25 bg-red-500/[0.04] p-5">
              <h3 className="font-heading text-lg font-bold text-slate-100 mb-1 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-red-400" /> Concepts players struggle with
              </h3>
              <p className="text-xs text-slate-500 mb-3">Negative average impact — good candidates for targeted teaching.</p>
              {struggles.length > 0 ? (
                <div className="space-y-2">
                  {struggles.map((c) => (
                    <button key={c.concept} data-testid={`concept-row-${c.concept.replace(/\s+/g, "-")}`}
                      onClick={() => openConcept(c.concept)}
                      className="w-full flex items-center justify-between gap-2 text-sm rounded-lg px-2 py-1.5 -mx-2 hover:bg-red-500/10 transition-colors group text-left">
                      <span className="text-slate-300 capitalize truncate flex items-center gap-1">
                        <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-red-400 transition-colors" />
                        {c.concept} <span className="text-slate-600">· {c.count}</span>
                      </span>
                      <span className="flex items-center gap-3 shrink-0">
                        <span className="text-[11px] text-slate-500">{c.positive_pct}% good</span>
                        <span className="font-mono font-semibold text-red-400 w-16 text-right">{fmt(c.avg_impact)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm py-6 text-center">No concepts show a negative average — players are handling them all well.</p>
              )}
            </div>
          </div>

          <CountryImprovement data={countryStats} days={days} />

          {/* Group / cohort performance */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5 mt-4" data-testid="admin-groups">
            <h3 className="font-heading text-lg font-bold text-slate-100 mb-1 flex items-center gap-2">
              <Layers className="w-4 h-4 text-indigo-400" /> Group / cohort performance
            </h3>
            <p className="text-xs text-slate-500 mb-3">Compare how facilitator cohorts are doing {days ? `in the last ${days} days` : "across all time"}.</p>
            {groups.length > 0 ? (
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-sm" data-testid="admin-groups-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                      <th className="py-2 pr-3 font-semibold">Group</th>
                      <th className="py-2 pr-3 font-semibold">Facilitator</th>
                      <th className="py-2 pr-3 font-semibold text-center">Players</th>
                      <th className="py-2 pr-3 font-semibold text-center">Completed</th>
                      <th className="py-2 pr-3 font-semibold text-right">Avg NW</th>
                      <th className="py-2 pr-3 font-semibold text-right">Avg Change</th>
                      <th className="py-2 pr-3 font-semibold text-right">% Grew</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr key={g.group_id} className="border-b border-slate-800/60">
                        <td className="py-2 pr-3 text-slate-200 font-medium">{g.name}</td>
                        <td className="py-2 pr-3 text-slate-400">{g.facilitator}</td>
                        <td className="py-2 pr-3 text-center text-slate-300">{g.players}</td>
                        <td className="py-2 pr-3 text-center text-slate-300">{g.completed}<span className="text-slate-600">/{g.games}</span></td>
                        <td className="py-2 pr-3 text-right font-mono text-slate-200">{fmt(g.avg_net_worth)}</td>
                        <td className={`py-2 pr-3 text-right font-mono font-semibold ${g.avg_change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {g.avg_change >= 0 ? "+" : ""}{fmt(g.avg_change)}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-300">{g.grew_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-sm py-8 text-center">No group games in this period yet.</p>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-5 mt-8">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h3 className="font-heading text-lg font-bold text-slate-100">Recent sessions (drill-down)</h3>
            <div className="flex gap-2">
              <Button data-testid="export-anon-btn" onClick={() => doExport(true)} disabled={exporting} variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 h-9 text-xs">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Anonymised CSV
              </Button>
              <Button data-testid="export-ident-btn" onClick={() => doExport(false)} disabled={exporting} variant="outline" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 h-9 text-xs">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Identifiable CSV
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-sessions-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="py-2 pr-3 font-semibold">Player</th>
                  <th className="py-2 pr-3 font-semibold">Email</th>
                  <th className="py-2 pr-3 font-semibold">Status</th>
                  <th className="py-2 pr-3 font-semibold">Stage</th>
                  <th className="py-2 pr-3 font-semibold text-right">Net Worth</th>
                  <th className="py-2 pr-3 font-semibold">Persona</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-b border-slate-800/60">
                    <td className="py-2 pr-3 text-slate-200">{s.nickname}</td>
                    <td className="py-2 pr-3 text-slate-500 truncate max-w-[160px]">{s.email || "—"}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-[11px] font-semibold ${s.status === "complete" ? "text-emerald-400" : "text-yellow-400"}`}>
                        {s.status === "complete" ? "Complete" : `R${s.current_round}`}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-slate-400 capitalize">{(s.life_stage || "—").replace(/_/g, " ")}</td>
                    <td className="py-2 pr-3 text-right font-mono text-slate-200">{fmt(s.net_worth)}</td>
                    <td className="py-2 pr-3 text-slate-400">{s.persona || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
